import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import logoGoldHorizontal from '../imgs/logo-gold-horizontal.png';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  // Always force dark mode
  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigationItems = [
    { title: 'الرئيسية', path: '/' },
    { title: 'خدماتنا', path: '/services' },
    { title: 'باقاتنا', path: '/packages' },
    { title: 'المقالات', path: '/blog' },
    { title: 'من نحن', path: '/about' },
    { title: 'تواصل معنا', path: '/contact' }
  ];

  const socialLinks = [
    {
      icon: 'facebook',
      href: 'https://www.facebook.com/profile.php?id=61572896888022',
    },
    {
      icon: 'instagram',
      href: 'https://www.instagram.com/nidsoft_agency/',
    },
    {
      icon: 'twitter',
      href: 'https://x.com/Nidal_Raed7',
    },
    {
      icon: 'whatsapp',
      href: 'https://wa.me/962780785034',
    },
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Nidsoft Agency",
    "url": "https://nidsoft-agency.web.app",
    "logo": "https://nidsoft-agency.web.app/N.png",
    "sameAs": socialLinks.map(link => link.href)
  };

  const sitelinksSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "url": "https://nidsoft-agency.web.app",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://nidsoft-agency.web.app/?s={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    }
  };

  const navigationSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": navigationItems.map((item, index) => ({
      "@type": "SiteNavigationElement",
      "position": index + 1,
      "name": item.title,
      "url": `https://nidsoft-agency.web.app${item.path === '/' ? '' : item.path}`
    }))
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(sitelinksSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(navigationSchema)}
        </script>
      </Helmet>

      <motion.header
        className="fixed w-full z-50 transition-all duration-500 top-0 left-0 pt-0 md:pt-3 pointer-events-none"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        role="banner"
      >
        {/* Top Contact Bar - Collapsible on Scroll */}
        <div className={`hidden md:block transition-all duration-300 pointer-events-auto bg-primary/10 backdrop-blur-sm border-b border-secondary/5 overflow-hidden ${isScrolled ? 'h-0 opacity-0' : 'h-11 opacity-100'
          }`}>
          <div className="container mx-auto px-6 h-full flex items-center justify-between">
            <div className="flex items-center gap-6" dir="ltr">
              <a
                href="tel:+962780785034"
                className="flex items-center gap-2.5 text-light hover:text-secondary transition-all duration-300 group text-xs md:text-sm font-sans"
                aria-label="اتصل بنا على الرقم 962780785034+"
              >
                <span className="w-6.5 h-6.5 rounded-full bg-secondary/10 flex items-center justify-center group-hover:bg-secondary/20 transition-all duration-300">
                  <i className="fas fa-phone text-secondary text-[10px]" aria-hidden="true"></i>
                </span>
                <span className="font-semibold tracking-wide">+962 780 785 034</span>
              </a>
              <a
                href="mailto:info@nidsoft.com"
                className="flex items-center gap-2.5 text-light hover:text-secondary transition-all duration-300 group text-xs md:text-sm font-sans"
                aria-label="راسلنا على البريد الإلكتروني info@nidsoft.com"
              >
                <span className="w-6.5 h-6.5 rounded-full bg-secondary/10 flex items-center justify-center group-hover:bg-secondary/20 transition-all duration-300">
                  <i className="fas fa-envelope text-secondary text-[10px]" aria-hidden="true"></i>
                </span>
                <span className="font-semibold tracking-wide">info@nidsoft.com</span>
              </a>
            </div>

            <div className="hidden md:flex items-center gap-4">
              <span className="text-[11px] text-accent-muted font-semibold">تابعنا على وسائل التواصل:</span>
              <div className="flex items-center gap-3">
                {socialLinks.map((social) => (
                  <a
                    key={social.icon}
                    href={social.href}
                    className="w-10 h-10 rounded-xl bg-secondary/5 border border-secondary/10 flex items-center justify-center hover:bg-secondary hover:border-secondary group transition-all duration-300"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`تابعنا على ${social.icon === 'facebook' ? 'فيسبوك' : social.icon === 'instagram' ? 'إنستغرام' : social.icon === 'twitter' ? 'تويتر' : social.icon === 'whatsapp' ? 'واتساب' : social.icon}`}
                  >
                    <i className={`fab fa-${social.icon} text-secondary text-sm group-hover:text-primary transition-colors duration-300`} aria-hidden="true" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Main Floating Glassmorphic Navbar */}
        <div className="w-full px-4 md:px-6 pt-3">
          <div
            className={`transition-all duration-500 pointer-events-auto mx-auto border ${isScrolled
              ? 'py-2.5 px-6 bg-primary-light/60 backdrop-blur-xl border-secondary/15 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.3)] max-w-6xl'
              : 'py-4 px-5 bg-primary-light/20 backdrop-blur-md border-secondary/5 rounded-2xl max-w-7xl'
              }`}
            role="navigation"
            aria-label="القائمة الرئيسية"
          >
            <div className="flex items-center justify-between mx-auto">

              {/* Left Side: CTA Buttons */}
              <div className="hidden md:flex items-center gap-4 md:order-3">
                <a
                  href="https://wa.me/962780785034"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative group inline-flex"
                  aria-label="تواصل معنا عبر واتساب للحصول على استشارة مجانية"
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-secondary to-secondary-light rounded-lg blur opacity-75 group-hover:opacity-100 transition-opacity duration-300" />
                  <span className="relative inline-flex items-center px-4 py-2 rounded-lg bg-primary border border-secondary text-secondary group-hover:bg-gradient-to-r from-secondary to-secondary-light group-hover:text-primary font-bold text-sm transition-all duration-300 btn-gold-shine">
                    <span>استشارة مجانية</span>
                    <i className="fab fa-whatsapp mr-2 text-base animate-pulse" aria-hidden="true" />
                  </span>
                </a>
              </div>

              {/* Mobile call link - right side (first in RTL order, visual right) */}
              <div className="w-[100px] md:hidden flex justify-start items-center gap-3">
                <a
                  href="tel:+962780785034"
                  className="relative group"
                  aria-label="اتصل بنا هاتفياً"
                >
                  <div className="w-9 h-9 rounded-full bg-gradient-to-r from-secondary to-secondary-light flex items-center justify-center text-primary shadow-lg">
                    <i className="fas fa-phone text-sm animate-pulse" aria-hidden="true"></i>
                  </div>
                </a>
              </div>

              {/* Center Side: Navigation Menu links */}
              <div className="hidden md:flex items-center gap-8 md:order-2">
                {navigationItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="relative group py-2"
                  >
                    <span className={`text-base font-medium ${location.pathname === item.path
                      ? 'text-secondary'
                      : 'text-light/80 group-hover:text-light'
                      } transition-colors duration-300`}>
                      {item.title}
                    </span>
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-secondary to-secondary-light group-hover:w-full transition-all duration-300" />
                    {location.pathname === item.path && (
                      <motion.span
                        layoutId="activeNav"
                        className="absolute -bottom-1 left-0 w-full h-0.5 bg-gradient-to-r from-secondary to-secondary-light"
                      />
                    )}
                  </Link>
                ))}
              </div>

              {/* Right Side: Glowing Logo brand */}
              <div className="flex items-center md:order-1">
                <Link to="/" className="block">
                  <div className="flex items-center">
                    <img
                      src={logoGoldHorizontal}
                      alt="Nidsoft Agency"
                      className="h-10 md:h-12 w-auto object-contain hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                </Link>
              </div>

              {/* Mobile CTA support (headset) - last in RTL order, visual left */}
              <div className="w-[100px] md:hidden flex justify-end items-center gap-3">
                <Link
                  to="/contact"
                  className="relative group"
                  aria-label="اتصل بنا للحصول على المساعدة"
                >
                  <div className="w-9 h-9 rounded-full bg-gradient-to-r from-secondary to-secondary-light flex items-center justify-center text-primary shadow-lg">
                    <i className="fas fa-headset text-sm" aria-hidden="true"></i>
                  </div>
                </Link>
              </div>

            </div>
          </div>
        </div>


      </motion.header>
    </>
  );
};

export default Header;